import { TFunction, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  Paper,
  Tooltip,
} from "@material-ui/core";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import ButtonWithLoading from "components/ButtonWithLoading";
import { useRuleContext } from "./context/ruleContext";
import { canRestart } from "./context/helpers";
import { getChanges } from "./ConfirmUpdateModal/state/getChanges";

import { State } from "./context/types";
import { Rule, RuleUrlParams } from "../common/types";
import { ChangeEvent } from "react";

export default function SaveButton() {
  const { t } = useTranslation("rules");
  const { state, dispatch } = useRuleContext();
  const { action }: RuleUrlParams = useParams();
  const isEdit = action === "edit";
  const containerPadding = !isEdit ? "15px  24px 25px 24px" : "25px 24px";

  function onSave() {
    if (!isEdit) return dispatch({ type: "saveRule" });

    const { savedRule } = state.editData;
    const changes = getChanges(savedRule, state.rule);

    if (changes.length > 0) {
      dispatch({
        type: "updateEditData",
        payload: { showUpdateConfirm: true, changes },
      });
    } else {
      dispatch({ type: "saveRule" });
    }
  }

  function onSaveAsDisabled(
    _: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) {
    dispatch({ type: "updateExecutionStatus", payload: !checked });
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs>
        <Paper style={{ padding: containerPadding }}>
          {!isEdit && (
            <Box mb={2}>
              <FormControlLabel
                label={t("saveWithoutExecuting.label")}
                control={
                  <Checkbox
                    checked={!state.rule.enabled}
                    color="primary"
                    size="small"
                    onChange={onSaveAsDisabled}
                    disabled={!state.canSave}
                  />
                }
              />
              <FormHelperText style={{ marginTop: ".5px" }}>
                {t("saveWithoutExecuting.helper")}
              </FormHelperText>
            </Box>
          )}
          <Box display="grid" gridTemplateColumns="3fr auto">
            <ButtonWithLoading
              color="primary"
              data-testid="rules__save-btn"
              disableElevation
              fullWidth
              size="large"
              variant="contained"
              loading={state.asyncStatus === "saving"}
              disabled={cannotSave(state, action)}
              onClick={onSave}
            >
              {t("saveRule")}
            </ButtonWithLoading>
            {cannotSave(state, action) && (
              <Tooltip title={errorHelp(state, action, t)} placement="top">
                <Box alignSelf="center" pl={2} pt="5px" color="error.main">
                  <ReportProblemOutlinedIcon />
                </Box>
              </Tooltip>
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}

function cannotSave(state: State, action: RuleUrlParams["action"]) {
  if (!state.canSave) return true;
  return isNotEditable(state.rule, action);
}

function isNotEditable(rule: Rule, action: RuleUrlParams["action"]) {
  if (action !== "copy" && !canRestart(rule)) return true;
  return false;
}

function errorHelp(
  state: State,
  action: RuleUrlParams["action"],
  t: TFunction
) {
  if (isNotEditable(state.rule, action)) {
    return t("saveBtnHelper.notEditable");
  }

  if (state.remaining.length === 2) {
    return t("saveBtnHelper.all");
  }
  if (state.remaining.includes("steps")) {
    return t("saveBtnHelper.steps");
  }

  if (state.remaining.includes("name")) {
    return t("saveBtnHelper.name");
  }

  if (state.filters.targetFilter.errors) {
    return t("saveBtnHelper.targetFilter");
  }

  return null;
}
